import {
  ArrowLeftIcon,
  MicrophoneIcon,
  PaperClipIcon,
} from '@heroicons/react/outline';
import type { ReactNode } from 'react';

import styles from '../../agents.module.css';

import clsxm from '@/lib/clsxm';

import PhoneFrame from './PhoneFrame';
import type { DemoBrand } from '../types';
import { useChannelsT } from '../useChannelsT';
import { delay } from '../../primitives';

export type TelegramLine =
  | {
      from: 'customer';
      text: ReactNode;
      at: number;
      time: string;
      /** A photo the customer sent, drawn above the caption. */
      photo?: ReactNode;
    }
  | {
      from: 'agent';
      text: ReactNode;
      at: number;
      time: string;
      typingAt?: number;
    };

/**
 * A customer's chat with the business's Telegram bot, in Telegram's own
 * colours: the pastel wallpaper, green outgoing bubbles and the bot's Menu
 * button next to the message box.
 */
export default function TelegramChat({
  brand,
  lines,
}: {
  brand: DemoBrand;
  lines: TelegramLine[];
}) {
  const { t } = useChannelsT();

  return (
    <PhoneFrame bar='bg-white text-[#000] dark:bg-[#17212b] dark:text-white'>
      <div className='flex items-center gap-2.5 bg-white px-3 pb-2.5 pt-1 text-[#000] dark:bg-[#17212b] dark:text-white'>
        <ArrowLeftIcon className='h-5 w-5 flex-none text-[#3390ec] rtl:-scale-x-100 dark:text-[#6ab3f3]' />
        <span className='min-w-0 flex-1 text-center'>
          <span className='block truncate text-[15px] font-semibold leading-tight'>
            {brand.name}
          </span>
          <span className='block truncate text-[11.5px] leading-tight text-[#707579] dark:text-[#7d8b99]'>
            {t('bot')}
          </span>
        </span>
        <span
          className={clsxm(
            'flex h-9 w-9 flex-none items-center justify-center rounded-full text-[15px] font-semibold text-white',
            brand.bg,
          )}
        >
          {brand.initial}
        </span>
      </div>

      <div className='relative flex min-h-[25rem] flex-col justify-end gap-1.5 bg-[linear-gradient(165deg,#d5e7b4_0%,#b4d8c7_45%,#c2d3ee_100%)] px-2.5 pb-2 pt-3 text-[13.5px] leading-[1.35] text-[#000] dark:bg-[linear-gradient(165deg,#0f1c26_0%,#14232f_55%,#101b29_100%)] dark:text-white'>
        <span
          aria-hidden='true'
          className='pointer-events-none absolute inset-0 opacity-40 [background-image:radial-gradient(rgba(255,255,255,0.55)_1.2px,transparent_1.6px)] [background-size:18px_18px] dark:opacity-10'
        />
        <p
          className={clsxm(
            styles.enter,
            'relative mx-auto mb-1 rounded-full bg-black/20 px-2.5 py-0.5 text-[11px] font-medium text-white dark:bg-white/10',
          )}
          style={delay(150)}
        >
          {t('Today')}
        </p>

        {lines.map((line, index) =>
          line.from === 'customer' ? (
            <div key={index} className='relative flex justify-end'>
              <div
                className={clsxm(
                  styles.bubble,
                  styles.bubbleEnd,
                  'relative max-w-[84%] overflow-hidden rounded-[14px] rounded-ee-[4px] bg-[#e1ffc7] shadow-[0_1px_1px_rgba(0,0,0,0.12)] dark:bg-[#2b5278]',
                )}
                style={delay(line.at)}
              >
                {line.photo ? (
                  <div className='p-[3px] pb-0'>{line.photo}</div>
                ) : null}
                <div className='px-2.5 pb-[6px] pt-1.5'>
                  {line.text}
                  <span aria-hidden='true' className='inline-block w-[50px]' />
                </div>
                <span className='absolute bottom-1 end-2 text-[10.5px] leading-none tabular-nums text-[#4fae4e] dark:text-[#7dabd0]'>
                  <span dir='ltr' className='flex items-center gap-[3px]'>
                    {line.time}
                    <DoubleCheck />
                  </span>
                </span>
              </div>
            </div>
          ) : (
            <div key={index} className='relative grid'>
              {line.typingAt !== undefined && (
                <span
                  className={clsxm(
                    styles.typing,
                    'flex h-8 w-14 items-center justify-center gap-1 self-start rounded-[14px] rounded-es-[4px] bg-white shadow-[0_1px_1px_rgba(0,0,0,0.12)] [grid-area:1/1] dark:bg-[#182533]',
                  )}
                  style={delay(line.typingAt, {
                    '--len': `${line.at - line.typingAt}ms`,
                  })}
                >
                  <span className='h-1.5 w-1.5 rounded-full bg-[#8a9aa9]' />
                  <span className='h-1.5 w-1.5 rounded-full bg-[#8a9aa9]' />
                  <span className='h-1.5 w-1.5 rounded-full bg-[#8a9aa9]' />
                </span>
              )}
              <div className='flex justify-start [grid-area:1/1]'>
                <div
                  className={clsxm(
                    styles.bubble,
                    styles.bubbleStart,
                    'relative max-w-[84%] rounded-[14px] rounded-es-[4px] bg-white px-2.5 pb-[6px] pt-1.5 shadow-[0_1px_1px_rgba(0,0,0,0.12)] dark:bg-[#182533]',
                  )}
                  style={delay(line.at)}
                >
                  {line.text}
                  <span aria-hidden='true' className='inline-block w-[36px]' />
                  <span className='absolute bottom-1 end-2 text-[10.5px] leading-none tabular-nums text-[#a0acb6] dark:text-[#6d7f8f]'>
                    <span dir='ltr'>{line.time}</span>
                  </span>
                </div>
              </div>
            </div>
          ),
        )}
      </div>

      <div className='flex items-center gap-2 bg-white px-2.5 pb-4 pt-2 text-[#8a9aa9] dark:bg-[#17212b] dark:text-[#6d7f8f]'>
        <span className='flex-none rounded-full bg-[#3390ec] px-2.5 py-1 text-[12px] font-semibold text-white dark:bg-[#5288c1]'>
          {t('Menu')}
        </span>
        <span className='min-w-0 flex-1 truncate text-[14px]'>
          {t('Message')}
        </span>
        <PaperClipIcon className='h-5 w-5 flex-none -rotate-45' />
        <MicrophoneIcon className='h-5 w-5 flex-none' />
      </div>
    </PhoneFrame>
  );
}

function DoubleCheck() {
  return (
    <svg viewBox='0 0 16 11' aria-hidden='true' className='h-[11px] w-4'>
      <path
        d='M11.1.6 4.6 7.8 1.7 5 .6 6.1l4 3.9L12.2 1.7 11.1.6zm3.8 0L8.4 7.8l-.9-.9-1.1 1.1 2 2L16 1.7 14.9.6z'
        fill='currentColor'
      />
    </svg>
  );
}

/** A washing machine's display showing an error code, as a customer's photo. */
export function ErrorCodePhoto({ code }: { code: string }) {
  return (
    <div className='relative h-36 overflow-hidden rounded-[11px] bg-[linear-gradient(150deg,#e9ecef_0%,#cfd5db_55%,#b9c1c9_100%)]'>
      <span className='absolute -start-6 top-6 h-32 w-32 rounded-full bg-white/50 blur-xl' />
      <div className='absolute inset-x-6 top-7 rounded-xl bg-[#2d3338] p-2.5 shadow-[inset_0_2px_6px_rgba(0,0,0,0.5)]'>
        <div
          dir='ltr'
          className='flex items-center justify-between rounded-lg bg-[#10181c] px-3 py-2 font-mono text-[26px] font-bold leading-none tracking-[0.12em] text-[#ff6b3d] [text-shadow:0_0_10px_rgba(255,107,61,0.65)]'
        >
          {code}
          <span className='flex flex-col gap-1'>
            <span className='h-1.5 w-1.5 rounded-full bg-[#ff6b3d] shadow-[0_0_6px_#ff6b3d]' />
            <span className='h-1.5 w-1.5 rounded-full bg-[#3b4246]' />
          </span>
        </div>
      </div>
      <div className='absolute inset-x-8 bottom-4 flex justify-between'>
        {[0, 1, 2, 3].map((dot) => (
          <span
            key={dot}
            className='h-3.5 w-3.5 rounded-full bg-[#f4f6f8] shadow-[0_1px_2px_rgba(0,0,0,0.25)]'
          />
        ))}
      </div>
    </div>
  );
}
